import { OpenGovAppConfig } from '@opengov/app-config';
import { Button, Form, Icon, RadioButton, RadioGroup, Switch, Text } from '@opengov/component-library/capital';
import { IEntity } from '@opengov/og-api-ts';

import { ICurrentOrLegacySavedView } from '@opengov/reporting-legacy-module';
import { ICurrentOrLegacyReport, IMergedReport } from '@opengov/reporting-types';
import { IPublication, IStory, IStoryContent, IUpdatePayload } from '@opengov/stories-shared/types';
import cx from 'classnames2';
import React, { RefObject, forwardRef, useImperativeHandle, useRef } from 'react';
import { createPortal } from 'react-dom';
import Alert from 'react-s-alert';
import Select from 'react-select';
import { v1 as uuid } from 'uuid';

import {
  DETAIL_TABLE_MAX_COLUMN_LIMIT,
  EMBED_REQUEST_TYPE,
  EMBED_SYNC_SETTING,
  MEDIA_TABLE_TILE,
  REPORT_FETCH_ERROR,
  REQUEST_ERROR,
  SAVED_VIEW_FETCH_ERROR,
  TAG_DIV,
  TAG_SCRIPT
} from '../../../stories/constants';
import { IEmbedCreateEmbedPayload } from '../AddTile/types';
import { getReportType, removeMedia } from '../../../stories/utils';
import { ToastProps } from '../../../stories/components/Toast/Toast';
import {
  IEmbed,
  IEmbedDataUpdateFrequencyOption,
  IHandleEmbedPayload,
  IModalMessages,
  ISessionUser,
  IStoryEmbedsResponse,
  IUpdateStoryContentProps
} from '../../../types';
import { bumpPublicationUpdatedAt } from '../../../stories/components/Publications/PublicationView/hooks/usePublicationReportEmbedMutations';

import { ReportOption } from '../AddTile/ReportOption';
import { Modal } from '../Modal';
import { RemoveEmbedModal } from '../RemoveEmbedModal/RemoveEmbedModal';

import styles from './AddTableTile.scss';

type IReportInfoWithDimensional = ICurrentOrLegacyReport & {
  isDimensional?: boolean;
};

export interface IAddTableTileProps {
  isTemplate: boolean;
  isDetailsTableEmbedEnabled?: boolean;
  messages: IModalMessages;
  onClose(): void;
  reports: ICurrentOrLegacyReport[];
  isRequestingReports: boolean;
  getReports: (props: {
    entityId: number;
    includeAccountSummaryReports?: boolean;
    includeMultiDatasetReports?: boolean;
  }) => Promise<ICurrentOrLegacyReport[]>;
  entityId: number;
  isRequestingSavedViews: boolean;
  story: IStory;
  storyContent: IStoryContent;
  savedViews: ICurrentOrLegacySavedView[];
  getSavedViews: (props: {
    reportId?: string;
    legacyReportId?: string;
    reportVersion: string;
    entity?: IEntity;
  }) => Promise<ICurrentOrLegacySavedView[]>;
  entity: IEntity;
  embed: IEmbed;
  isCreatingEmbed: boolean;
  isFetchingEmbed: boolean;
  includeMultiDatasetReports: boolean;
  dataUpdateFrequencyOptions: IEmbedDataUpdateFrequencyOption[];
  getReportInfo: (props: { reportId: string }) => Promise<IReportInfoWithDimensional>;
  selectedEmbed: IStoryEmbedsResponse;
  isReportEmbedListSelected: boolean;
  entitySubdomain: string;
  storyContents: any;
  user: ISessionUser;
  publication?: IPublication;
  displayReportEmbeds?: boolean;
  resetEmbed(): void;
  resetCreateEmbedRequestStatus(): void;
  createEmbed(props: IEmbedCreateEmbedPayload): Promise<IEmbed>;
  cloneEmbedById(props: { id: string }): any;
  handleStoryEmbeds(props: IHandleEmbedPayload): void;
  deleteStoryEmbed(props: IUpdatePayload): Promise<{
    [data: string]: [{ embedId: string; storyId: string }];
  }>;
  updateStoryContent(storyContent: IStoryContent): void;
  updateStoryContentDB(storyContent: IUpdateStoryContentProps): void;
  addMedia(storyContent: IStoryContent, media: string, mediaIndex: number, mediaType: string): any;
  getStory({
    storyId,
    entitySubdomain,
    isTemplate
  }: {
    storyId: string;
    entitySubdomain: string;
    isTemplate: boolean;
  }): void;
  getStoryContent(payload: { storyId: string; entityId: number; entitySubdomain: string; isTemplate: boolean });
  shouldOnlyRenderInnerContent?: boolean;
  onFormDataChange?: (isValid: boolean) => void;
  onFormLoadingChange?: (isLoading: boolean) => void;
  toggleToast(toastProps: ToastProps | null): void;
}

const operateUrl = OpenGovAppConfig.OPERATE_URL;
export const reportResetValue = { id: null, name: '', version: '' };
export const savedViewResetValue = { id: null, name: '', description: '' };

interface IAddTableTileState {
  isUpdating: boolean;
  report: Partial<IMergedReport>;
  savedView: {
    id: string;
    name: string;
    description: string;
  };
  allowClickThroughEmbed: boolean;
  showReportLink?: boolean;
  embedId: string;
  disableClickThrough: boolean;
  title: string;
  description: string;
  embedSyncUpdateSetting: string;
  customTableTile: string;
  isLoadingValuesFromEmbed: boolean;
  displayAccountCodes: boolean;
  isAccountsCodeSwitchVisible: boolean;
  enableTableType: boolean;
  hasFailedReportEmbed: boolean;
  tableTypeRequest: EMBED_REQUEST_TYPE;
  isNonDimensionalReportSelected: boolean;
  storyContentToUpdate?: { content: IStoryContent; mediaIndex: number };
  showDeleteModal: boolean;
  storySelectedFromReportList: IStory;
  disableSaveButton: boolean;
  isDeleteEmbedInProgress: boolean;
  createEmbedController?: React.MutableRefObject<AbortController>;
}

export class AddTableTile extends React.Component<IAddTableTileProps, IAddTableTileState> {
  node: RefObject<HTMLDivElement>;
  constructor(props: IAddTableTileProps) {
    super(props);

    this.state = {
      isUpdating: false,
      report: reportResetValue,
      savedView: savedViewResetValue,
      embedId: '',
      allowClickThroughEmbed: true,
      disableClickThrough: false,
      title: '',
      description: '',
      embedSyncUpdateSetting: EMBED_SYNC_SETTING.SNAPSHOT,
      showReportLink: true,
      displayAccountCodes: false,
      isAccountsCodeSwitchVisible: false,
      customTableTile: '',
      isLoadingValuesFromEmbed: false,
      enableTableType: true,
      tableTypeRequest: null,
      hasFailedReportEmbed: false,
      isNonDimensionalReportSelected: false,
      showDeleteModal: false,
      storySelectedFromReportList: null,
      disableSaveButton: false,
      isDeleteEmbedInProgress: false,
      createEmbedController: React.createRef()
    };
    this.node = React.createRef();
  }

  createEmbedRequest = async () => {
    const {
      savedView,
      tableTypeRequest,
      customTableTile,
      report,
      displayAccountCodes,
      embedSyncUpdateSetting,
      createEmbedController
    } = this.state;
    const reportVersion = report.version === 'v2' ? 'v2' : 'v1';

    // Clear the previous attempt's failure here rather than in each caller, otherwise a retry that
    // succeeds (a different saved view or table type) keeps showing the stale failure banner.
    this.setState({ hasFailedReportEmbed: false });

    if (createEmbedController.current) {
      createEmbedController.current.abort();
    }
    const controller = new AbortController();
    createEmbedController.current = controller;

    void this.props
      .createEmbed({
        title: customTableTile,
        description: savedView.description,
        reportId: report.id,
        savedViewId: savedView.id,
        displayAccountCodes: displayAccountCodes,
        shouldSync: embedSyncUpdateSetting === EMBED_SYNC_SETTING.LIVE,
        reportVersion: reportVersion,
        type: tableTypeRequest,
        controller: createEmbedController.current
      })
      .catch((error) => {
        // Aborts are expected whenever the selection changes again before this settles; the effect
        // already surfaces real failures.
        if (error?.name !== 'AbortError') {
          this.setState({ hasFailedReportEmbed: true });
        }
      });
  };

  handleReportValueChange = async (value) => {
    const { getSavedViews, entity, resetEmbed, isDetailsTableEmbedEnabled, getReportInfo } = this.props;
    resetEmbed();
    if (!!value.id && !!value.name) {
      this.setState({
        report: value,
        savedView: savedViewResetValue,
        tableTypeRequest: null,
        enableTableType: true,
        isNonDimensionalReportSelected: false,
        hasFailedReportEmbed: false
      });
      if (value.version !== 'v2') {
        if (value.legacyReportType !== 'grid') {
          this.setState({
            isAccountsCodeSwitchVisible: true,
            displayAccountCodes: false
          });
        } else {
          this.setState({
            isAccountsCodeSwitchVisible: false,
            displayAccountCodes: false
          });
        }
        void getSavedViews({ legacyReportId: value.legacyReportId, reportVersion: value.version, entity });
      } else {
        this.setState({
          isAccountsCodeSwitchVisible: false,
          displayAccountCodes: false,
          enableTableType: true,
          tableTypeRequest: null,
          isNonDimensionalReportSelected: false
        });
        void getSavedViews({ reportId: value.id, reportVersion: value.version, entity });
        if (value.version === 'v2' && isDetailsTableEmbedEnabled) {
          const reportInfo = await getReportInfo({ reportId: value.id });
          if (!reportInfo.isDimensional) {
            this.setState({ isNonDimensionalReportSelected: true });
            return;
          }
        }
      }
    } else {
      this.setState({
        report: reportResetValue,
        savedView: savedViewResetValue,
        isAccountsCodeSwitchVisible: false,
        displayAccountCodes: false,
        enableTableType: true,
        tableTypeRequest: null
      });
    }
  };

  handleSavedValueChange = async (value) => {
    const { resetEmbed } = this.props;
    resetEmbed();
    if (!!value.id && !!value.name) {
      const name: string = value.name || '';
      let title = name.replace(/(\r\n|\n|\r)/gm, ' ');
      title = title.length > 60 ? `${title.substring(0, 57)}...` : title;
      if (this.state.isNonDimensionalReportSelected) {
        this.setState({
          savedView: value,
          customTableTile: title,
          enableTableType: false,
          tableTypeRequest: null,
          disableSaveButton: false
        });
      } else {
        this.setState(
          {
            savedView: value,
            customTableTile: title,
            tableTypeRequest: EMBED_REQUEST_TYPE.SummaryTable,
            disableSaveButton: false
          },
          () => void this.createEmbedRequest()
        );
      }
    } else {
      this.setState({
        savedView: savedViewResetValue,
        customTableTile: savedViewResetValue.name,
        enableTableType: true,
        tableTypeRequest: null,
        disableSaveButton: false
      });
    }
  };

  onUpdateSettingChange = (value) => {
    const { embedSyncUpdateSetting } = this.state;

    if (value.id !== embedSyncUpdateSetting) {
      this.setState({ embedSyncUpdateSetting: value.id });
    }
  };

  handleSwitchSection = () => this.setState({ showReportLink: !this.state.showReportLink });

  handleAccountCodeSwitchSection = () => {
    this.setState({ displayAccountCodes: !this.state.displayAccountCodes }, () => {
      if (this.state.savedView.id) {
        void this.handleSavedValueChange(this.state.savedView);
      }
    });
  };

  handleSwitchControlPointer = (value) => {
    this.node.current.style.pointerEvents = value;
    if (value === 'auto') {
      this.node.current.style.zIndex = '0';
    }
  };

  renderReportDropdown = (): JSX.Element => {
    const { reports, messages, isRequestingReports, isFetchingEmbed, isCreatingEmbed } = this.props;
    const { report, hasFailedReportEmbed } = this.state;
    return (
      <div className={styles.reportDropdownContainer}>
        <Form.Item label={messages.report}>
          <Select
            name="report-table"
            labelKey="name"
            valueKey="id"
            options={reports}
            isLoading={isRequestingReports}
            placeholder={messages.selectReport}
            loadingPlaceholder={messages.searchingForReports}
            noResultsText={messages.noReports}
            onChange={this.handleReportValueChange}
            value={report.id as any}
            resetValue={reportResetValue}
            className="report-table-select"
            optionComponent={ReportOption}
            autoFocus={true}
            disabled={isCreatingEmbed || isFetchingEmbed}
          />
        </Form.Item>
        {hasFailedReportEmbed && (
          <div className={styles.errorFailedEmbed}>
            <Icon icon="status-warning" className={styles.errorIcon} />
            {messages.reportFailedToLoad}
          </div>
        )}
      </div>
    );
  };

  renderSavedViewSection = (): JSX.Element => {
    const { messages, savedViews, isRequestingSavedViews, isCreatingEmbed, isFetchingEmbed, embed } = this.props;

    const privateUrl = embed.privateUrl ?? embed?.data?.privateUrl;
    const publicUrl = embed.publicUrl ?? embed?.data?.publicUrl;

    const { savedView, report, hasFailedReportEmbed } = this.state;
    return (
      <Form.Item label={messages.savedView}>
        <div className={styles.savedViewDropdownRow}>
          <div
            className={styles.savedViewDropdownContainer}
            onClick={() => this.handleSwitchControlPointer('none')}
            role="presentation"
          >
            <Select
              name="savedView"
              labelKey="name"
              valueKey="id"
              options={savedViews}
              isLoading={isRequestingSavedViews}
              placeholder={messages.selectSavedView}
              loadingPlaceholder={messages.searchingForSavedViews}
              noResultsText={messages.noSavedViews}
              onChange={this.handleSavedValueChange}
              value={savedView.id as any}
              resetValue={savedViewResetValue}
              className={cx({ [styles.savedViewDropdownDisabled]: !report.id })}
              disabled={!report.id || isCreatingEmbed || isFetchingEmbed}
            />
          </div>
          <div className={styles.savedViewButton}>
            {savedView?.id && !hasFailedReportEmbed && (publicUrl || privateUrl) ? (
              <a href={publicUrl || privateUrl} target="_blank" rel="noopener noreferrer">
                <Button>{messages.goToView}</Button>
              </a>
            ) : (
              <Button disabled={true}>{messages.goToView}</Button>
            )}
          </div>
        </div>
      </Form.Item>
    );
  };

  handleTableType = (value) => {
    this.setState({ tableTypeRequest: value }, () => {
      void this.createEmbedRequest();
    });
  };

  renderWarningMessage = (): JSX.Element => {
    const { tableTypeRequest, enableTableType } = this.state;
    const { embed } = this.props;
    const {
      detailTableWarningNote,
      detailTableMaxLimitMessage1,
      detailTableMaxRowLimit,
      detailTableMaxLimitMessage2,
      viewReportLink,
      detailTableMaxLimitMessage3,
      detailTableMaxLimitMessage4,
      goToView,
      tableTypeAutoSelectionWarning
    } = this.props.messages;
    if (enableTableType && tableTypeRequest === EMBED_REQUEST_TYPE.SummaryTable) {
      return (
        <div className={styles.detailTableMaxLimitMessagecontainer} data-test="detail-table-max-limit-message">
          <div>
            <Icon icon="status-warning" className={styles.warningIcon} />
          </div>
          <Text size="minimum" font="condensed">
            {tableTypeAutoSelectionWarning}
          </Text>
        </div>
      );
    }
    if (
      embed?.type === EMBED_REQUEST_TYPE.DetailTable &&
      tableTypeRequest === EMBED_REQUEST_TYPE.DetailTable &&
      embed?.data?.sdk?.componentProps?.data?.columns.length <= DETAIL_TABLE_MAX_COLUMN_LIMIT
    ) {
      return (
        <div className={styles.detailTableMaxLimitMessagecontainer} data-test="detail-table-max-limit-message">
          <div>
            <Icon icon="status-warning" className={styles.warningIcon} />
          </div>
          <Text size="minimum" font="condensed">
            <span className={styles.detailTableMessageHighlights}>{detailTableWarningNote}</span>
            {detailTableMaxLimitMessage1}
            <span className={styles.detailTableMessageHighlights}>{detailTableMaxRowLimit}</span>
            {detailTableMaxLimitMessage2}
            <span className={styles.detailTableMessageHighlights}>{viewReportLink}</span>
            {detailTableMaxLimitMessage3}
            <span className={styles.detailTableMessageHighlights}>{goToView}</span>
            {detailTableMaxLimitMessage4}
          </Text>
        </div>
      );
    }
  };

  renderTableType = (): JSX.Element => {
    const { selectTableTypeName, selectTableTypeTitle, summaryTableLabel, detailsTableLabel } = this.props.messages;
    const { enableTableType, tableTypeRequest } = this.state;
    return (
      <div className={styles.dataFrequencyDropdown}>
        <Form.Item label={selectTableTypeTitle}>
          <section>
            <RadioGroup
              disabled={enableTableType}
              onChange={this.handleTableType}
              dataTest="data-test-table-type"
              value={tableTypeRequest}
            >
              <RadioButton.Small
                name={selectTableTypeName}
                label={summaryTableLabel}
                value={EMBED_REQUEST_TYPE.SummaryTable}
                className={styles.selectTableType}
                dataTest="data-summary-table-type"
              />
              <RadioButton.Small
                name={selectTableTypeName}
                label={detailsTableLabel}
                value={EMBED_REQUEST_TYPE.DetailTable}
                className={styles.selectTableType}
                dataTest="data-details-table-type"
              />
            </RadioGroup>
          </section>
          {this.renderWarningMessage()}
        </Form.Item>
      </div>
    );
  };

  renderDataUpdateFrequencyDropdown = (): JSX.Element => {
    const { messages, dataUpdateFrequencyOptions } = this.props;
    const { embedSyncUpdateSetting } = this.state;

    return (
      <Form.Item className={styles.dataFrequencyDropdown} label={messages.updateSetting}>
        <div onClick={() => this.handleSwitchControlPointer('none')} role="presentation">
          <Select
            clearable={false}
            name="embedSyncUpdateSetting"
            labelKey="name"
            valueKey="id"
            options={dataUpdateFrequencyOptions}
            onChange={this.onUpdateSettingChange}
            value={embedSyncUpdateSetting}
            resetValue={{ id: '', value: '' }}
            className="test-report-table-data-update-setting-select"
          />
        </div>
      </Form.Item>
    );
  };

  handleCancel = () => {
    this.setState({ disableSaveButton: false });
    this.handleModalClose();
  };

  handleAdd = async () => {
    // Handle add button
    const {
      entityId,
      isTemplate,
      messages,
      story,
      storyContent,
      isReportEmbedListSelected,
      selectedEmbed,
      publication,
      displayReportEmbeds,
      user,
      deleteStoryEmbed,
      updateStoryContent,
      updateStoryContentDB,
      handleStoryEmbeds,
      addMedia,
      toggleToast
    } = this.props;
    const {
      showReportLink,
      embedSyncUpdateSetting,
      customTableTile,
      displayAccountCodes,
      tableTypeRequest,
      report,
      savedView,
      storyContentToUpdate,
      isUpdating
    } = this.state;
    let currentStoryContent = storyContent;
    const embed = this.props.embed.data.hasOwnProperty('version') ? this.props.embed.data : this.props.embed;
    const { id: embedId, publicUrl = null, privateUrl = null, version } = embed;
    let { mediaIndex = 0 } = storyContent;
    if (isReportEmbedListSelected && storyContentToUpdate?.content) {
      mediaIndex = storyContentToUpdate.content.mediaIndex || 0;
      currentStoryContent = storyContentToUpdate.content;
    }
    let tableTitle = customTableTile || '';
    tableTitle = tableTitle.replace(/\\/g, ``);
    const tableEmbedTitle = tableTitle.replace(/'/g, `\\'`).replace(/"/g, `\\"`);

    const embedCreationPayload = isUpdating
      ? {
        title: customTableTile,
        description: savedView.description,
        reportId: report.id,
        savedViewId: savedView.id,
        displayAccountCodes: displayAccountCodes,
        shouldSync: embedSyncUpdateSetting === EMBED_SYNC_SETTING.LIVE,
        reportVersion: embed.report_version,
        type: tableTypeRequest
      }
      : {
        id: embedId,
        title: customTableTile,
        description: '',
        privateUrl,
        publicUrl,
        shouldSync: embedSyncUpdateSetting === EMBED_SYNC_SETTING.LIVE,
        type: tableTypeRequest,
        chartType: tableTypeRequest,
        reportVersion: embed.report_version,
        groupDataBy: 'category',
        tableColumns: [],
        showUpdatedPreview: false,
        displayAccountCodes: displayAccountCodes,
        isReportEmbedSelected: isReportEmbedListSelected,
        reportTitle: selectedEmbed?.title,
        addOrUpdateEmbed: isReportEmbedListSelected
      };
    this.setState({ disableSaveButton: true });

    // on update embed create a new embed
    try {
      const embedResponse = await this.props.createEmbed(embedCreationPayload as any);
      // Without an id the tile below would be written into the story as data-embed-id="undefined",
      // which permanently breaks it. Fail before anything is persisted.
      if (!embedResponse?.id) {
        throw new Error('Created embed is missing an id');
      }
      // Update storyContent again when updateEmbed is finished, to make sure it's set to live update
      const selectedStory = story._id ? story : this.state.storySelectedFromReportList;
      const uniqueId = uuid();
      const mediaDiv = `
      <div
        id="embed-${uniqueId}"
        ${report.isPrivate ? ` data-warning="${messages.tileWarning}"` : ''}
        data-update-setting="${embedSyncUpdateSetting}"
        tabIndex="0"
        data-embed-id="${embedResponse.id}"
        data-embed-disable-click="${showReportLink}"
        data-embed-show-report-link="${showReportLink}"
        data-embed-display-account-codes="${displayAccountCodes}"
        data-embed-title="${tableEmbedTitle}"
        aria-label="${tableEmbedTitle}"
      />
    `;
      const mediaScript = `
      <script type="text/javascript" data-embed-id="${embedResponse.id}" data-selector="#embed-${uniqueId}">
        OpenGovSDK.embed({
          version: '${version === 'v2' ? 'v2' : 'v1'}',
          baseUrl: '${operateUrl}',
          embed: {
            id: '${embedResponse.id}',
            type: 'dashboardTile',
          },
          targetSelector: '#embed-${uniqueId}',
          disableClick: ${showReportLink},
          showReportLink: ${showReportLink},
          displayAccountCodes: ${displayAccountCodes},
          title: '${tableEmbedTitle}'
        });
      </script>
    `;
      const metaStoryContent = removeMedia(currentStoryContent, mediaIndex);
      const newStoryContent: IStoryContent = addMedia(metaStoryContent, mediaDiv, mediaIndex, MEDIA_TABLE_TILE);
      const finalStoryContent: IStoryContent = addMedia(newStoryContent, mediaScript, mediaIndex, MEDIA_TABLE_TILE);
      await updateStoryContent(finalStoryContent);
      await updateStoryContentDB({ story: selectedStory, isTemplate, storyContent: finalStoryContent });
      let handleStoryEmbedPayload = {
        story: { ...story, embeds: (story.embeds ?? []).concat(embed) },
        isTemplate,
        entityId
      } as IHandleEmbedPayload;
      if (displayReportEmbeds) {
        handleStoryEmbedPayload = {
          ...handleStoryEmbedPayload,
          storyEmbedUpdatePayload: {
            [embedResponse.id]: {
              id: embedResponse.id,
              storyId: story?._id || selectedEmbed?.story._id,
              frequency: embedSyncUpdateSetting,
              type: tableTypeRequest,
              reportId: report.id,
              savedViewId: savedView.id,
              authorId: story.author._id,
              isUpdating: this.state.isUpdating
            }
          },
          isUpdatingReportEmbed: isReportEmbedListSelected,
          reportTitle: report.name,
          publicationId: publication?.id,
          displayReportEmbeds
        };
      }
      if (isUpdating && displayReportEmbeds) {
        const embedIdForUpdate = !!selectedEmbed ? selectedEmbed.embedId : this.state.embedId;
        await deleteStoryEmbed({ storyId: story._id, embedId: embedIdForUpdate });
      }
      await handleStoryEmbeds(handleStoryEmbedPayload);
    } catch {
      this.setState({ disableSaveButton: false });
      this.showErrorMessage(messages.errorEmbedAddUpdate);
      return;
    }
    if (publication?.id) void bumpPublicationUpdatedAt(publication.id, user.id);
    if (isUpdating && isReportEmbedListSelected) {
      toggleToast({
        title: `${messages.report} ${getReportType(tableTypeRequest)} ${messages.updated}`,
        severity: 'success'
      });
    }
    this.handleCancel();
  };

  handleDeleteModalClose = async () => {
    this.setState({ showDeleteModal: false });
  };

  handleEmbedRemove = async () => {
    this.setState({ showDeleteModal: true });
  };

  handleRemove = async () => {
    const {
      isTemplate,
      story,
      entityId,
      storyContent,
      storyContent: { mediaIndex = 0 },
      isReportEmbedListSelected,
      publication,
      displayReportEmbeds,
      user,
      handleStoryEmbeds,
      updateStoryContent,
      updateStoryContentDB,
      deleteStoryEmbed
    } = this.props;
    this.setState({ isDeleteEmbedInProgress: true });
    const { storyContentToUpdate, showDeleteModal } = this.state;
    let currentMediaIndex = mediaIndex;
    let currentStoryContent = storyContent;
    if (isReportEmbedListSelected && storyContentToUpdate?.content) {
      currentMediaIndex = storyContentToUpdate.mediaIndex;
      currentStoryContent = storyContentToUpdate.content;
    }
    if (currentMediaIndex !== null) {
      await updateStoryContent(removeMedia(currentStoryContent, currentMediaIndex));
      await updateStoryContentDB({
        storyContent: removeMedia(currentStoryContent, currentMediaIndex),
        story: story,
        isTemplate: isTemplate
      });
    }

    // Handle removing embed from Story array
    await handleStoryEmbeds({
      story,
      isTemplate,
      entityId,
      isRemovingReportEmbed: isReportEmbedListSelected,
      reportTitle: this.state.report.name,
      publicationId: publication?.id,
      displayReportEmbeds
    });

    if (displayReportEmbeds)
      await deleteStoryEmbed({
        storyId: story._id,
        embedId: this.state.embedId ?? this.props.selectedEmbed.embedId,
        type: isReportEmbedListSelected && this.props.selectedEmbed.type,
        isReportEmbedListSelected,
        publicationId: publication.id
      });
    if (showDeleteModal) void this.handleDeleteModalClose();
    if (publication?.id) void bumpPublicationUpdatedAt(publication.id, user.id);
    this.setState({ isDeleteEmbedInProgress: false });
    this.handleCancel();
  };

  renderReportLinkSection = (): JSX.Element => {
    const { reportLinkText, no, yes } = this.props.messages;
    const { showReportLink } = this.state;
    return (
      <div
        className={styles.reportLinkContainer}
        role="presentation"
        onMouseOver={() => this.handleSwitchControlPointer('auto')}
      >
        <div className={styles.reportLinkSection}>{`${reportLinkText}`}</div>
        <div ref={this.node}>
          <Switch
            id="tableTileFormSwitch"
            checked={showReportLink}
            labelOn={yes}
            labelOff={no}
            onChange={this.handleSwitchSection}
            dataTest="report-link-switch"
          />
        </div>
      </div>
    );
  };

  renderDisplayAccountCodesSection = (): JSX.Element => {
    const { displayAccountCodesText, no, yes } = this.props.messages;
    const { displayAccountCodes, isLoadingValuesFromEmbed } = this.state;
    const { isCreatingEmbed } = this.props;
    return (
      <div
        className={styles.reportLinkContainer}
        role="presentation"
        onMouseOver={() => this.handleSwitchControlPointer('auto')}
      >
        <div className={styles.reportLinkSection}>{`${displayAccountCodesText}`}</div>
        <div ref={this.node}>
          <Switch
            id="tableTileFormAccountCodeSwitch"
            disabled={isCreatingEmbed || isLoadingValuesFromEmbed}
            checked={displayAccountCodes}
            labelOn={yes}
            labelOff={no}
            onChange={this.handleAccountCodeSwitchSection}
          />
        </div>
      </div>
    );
  };

  setEmbedProperties(mediaBlock) {
    let isUpdating = false;
    let embedSyncUpdateSetting;
    let disableClickThrough;
    let embedId;
    let showReportLink;
    let displayAccountCodes;
    let customTableTile;
    for (let i = 0; i < mediaBlock.children.length; i++) {
      if (mediaBlock.children[i].tagName === TAG_DIV && mediaBlock.children[i].hasAttribute('data-update-setting')) {
        isUpdating = true;
        embedSyncUpdateSetting = mediaBlock.children[i].getAttribute('data-update-setting') as EMBED_SYNC_SETTING;
      }

      if (
        mediaBlock.children[i].tagName === TAG_DIV &&
        mediaBlock.children[i].hasAttribute('data-embed-disable-click')
      ) {
        disableClickThrough = mediaBlock.children[i].getAttribute('data-embed-disable-click') === `true`;
        isUpdating = true;
      }

      if (mediaBlock.children[i].tagName === TAG_SCRIPT && mediaBlock.children[i].hasAttribute('data-embed-id')) {
        embedId = mediaBlock.children[i].getAttribute('data-embed-id');
        isUpdating = true;
      }
      if (
        mediaBlock.children[i].tagName === TAG_DIV &&
        mediaBlock.children[i].hasAttribute('data-embed-show-report-link')
      ) {
        showReportLink = mediaBlock.children[i].getAttribute('data-embed-show-report-link') === 'true';
      }
      if (
        mediaBlock.children[i].tagName === TAG_DIV &&
        mediaBlock.children[i].hasAttribute('data-embed-display-account-codes')
      ) {
        displayAccountCodes = mediaBlock.children[i].getAttribute('data-embed-display-account-codes') === 'true';
      }
      if (mediaBlock.children[i].tagName === TAG_DIV && mediaBlock.children[i].hasAttribute('data-embed-title')) {
        customTableTile = mediaBlock.children[i].getAttribute('data-embed-title');
      }
    }
    return {
      isUpdating,
      customTableTile,
      embedSyncUpdateSetting,
      disableClickThrough,
      showReportLink,
      displayAccountCodes,
      embedId
    };
  }

  setEmbedFieldsWhileUpdate(content: string, mediaIndex: number, storyContentToUpdate?: IStoryContent): void {
    const isUpdating = false;
    const embedSyncUpdateSetting = EMBED_SYNC_SETTING.SNAPSHOT;
    const showReportLink = true;
    const displayAccountCodes = false;
    const customTableTile = '';

    const fragment = document.createRange().createContextualFragment(content);
    // Find the media block inside the document fragment
    const disableClickThrough = false;
    const mediaBlocks = fragment.querySelectorAll('.media-block');
    const mediaBlock = mediaBlocks[0];
    const finalVal = this.setEmbedProperties(mediaBlock);
    this.setState(
      {
        isUpdating: finalVal.isUpdating || isUpdating,
        disableClickThrough: finalVal.disableClickThrough || disableClickThrough,
        embedId: finalVal.embedId || '',
        embedSyncUpdateSetting: finalVal.embedSyncUpdateSetting || embedSyncUpdateSetting,
        showReportLink: finalVal.showReportLink ?? showReportLink,
        customTableTile: finalVal.customTableTile || customTableTile,
        isLoadingValuesFromEmbed: finalVal.isUpdating,
        displayAccountCodes: finalVal.displayAccountCodes ?? displayAccountCodes,
        storyContentToUpdate: { content: storyContentToUpdate, mediaIndex }
      },
      () => {
        void this.fetchReportsAndSavedViews();
      }
    );
  }

  intializeStoryContentIfUpdateTile(): void {
    const {
      storyContent: { content, mediaIndex = 0 }
    } = this.props;
    // Default state
    const isUpdating = false;
    const embedSyncUpdateSetting = EMBED_SYNC_SETTING.SNAPSHOT;
    const showReportLink = true;
    const displayAccountCodes = false;
    const customTableTile = '';
    // Pull Embed ID from script attributes

    const fragment = document.createRange().createContextualFragment(content);
    // Find the media block inside the document fragment
    const mediaBlocks = fragment.querySelectorAll('.media-block');
    const mediaBlock = mediaBlocks[mediaIndex];

    // Pull Embed update setting from div attributes
    const disableClickThrough = false;
    const finalVal = this.setEmbedProperties(mediaBlock);
    this.setState(
      {
        isUpdating: finalVal.isUpdating || isUpdating,
        disableClickThrough: finalVal.disableClickThrough || disableClickThrough,
        embedId: finalVal.embedId || '',
        embedSyncUpdateSetting: finalVal.embedSyncUpdateSetting || embedSyncUpdateSetting,
        showReportLink: finalVal.showReportLink ?? showReportLink,
        customTableTile: finalVal.customTableTile || customTableTile,
        isLoadingValuesFromEmbed: finalVal.isUpdating,
        displayAccountCodes: finalVal.displayAccountCodes ?? displayAccountCodes
      },
      () => {
        void this.fetchReportsAndSavedViews();
      }
    );
  }

  intializeStoryContentIfUpdateFromReportEmbed = async (embed): Promise<void> => {
    const { embedId } = embed;
    const { entityId, entitySubdomain } = this.props;
    const storyContents = await this.props.getStoryContent({
      storyId: embed?.story?._id,
      entityId,
      entitySubdomain,
      isTemplate: false
    });
    this.setState({ storySelectedFromReportList: storyContents.story });
    const embedContent = storyContents.filter((z) => z.contentHtml?.indexOf(embedId) !== -1)[0];
    const mediaIndex = embedContent?.content?.match(/data-media-index="(.*?)"/)[1];

    if (!!embedContent?.content) this.setEmbedFieldsWhileUpdate(embedContent.content, mediaIndex - 1, embedContent);
  };

  handleTitle = (event: React.ChangeEvent<HTMLInputElement>): void => {
    event.preventDefault();
    this.setState({ customTableTile: event.target.value });
  };

  renderTitleInput = (): JSX.Element => {
    const { customTableTile, report } = this.state;
    const { messages } = this.props;
    const customTableTileLength = customTableTile?.length || 0;
    const tableTitle = customTableTile.replace(/\\'/g, `'`).replace(/\\"/g, `"`);
    return (
      <div className={styles.customInputTitleContainer}>
        <Form.Item label={messages.title}>
          <Form.CharacterCounter currentCount={customTableTileLength} maxCount={60} />
          <input
            type="text"
            name={messages.title}
            value={tableTitle}
            maxLength={60}
            onChange={this.handleTitle}
            data-test="summary-table-title"
            disabled={!report.id}
          />
        </Form.Item>
      </div>
    );
  };
  setSelectedReportAndSavedView = async (
    selectedSavedView: {
      id: string;
      name: string;
      description: string;
    },
    selectedReport: {
      id: string;
      name: string;
      version: string;
    },
    description: string,
    tableTypeRequest: EMBED_REQUEST_TYPE
  ): Promise<void> => {
    let enableTableType = true;
    if (selectedReport.version === 'v2') {
      const reportInfo = await this.props.getReportInfo({ reportId: selectedReport.id });
      if (!reportInfo.isDimensional) {
        enableTableType = false;
      }
    }
    this.setState({
      title: selectedReport?.name,
      description,
      report: {
        id: selectedReport?.id,
        name: selectedReport?.name,
        version: selectedReport?.version
      },
      savedView: {
        id: selectedSavedView?.id,
        name: selectedSavedView?.name,
        description: selectedSavedView?.description
      },
      isLoadingValuesFromEmbed: false,
      tableTypeRequest,
      enableTableType: enableTableType
    });
  };

  showErrorMessage = (text: string): void => {
    Alert.error(text, {
      timeout: 'none',
      customFields: {
        title: REQUEST_ERROR
      }
    });
  };

  fetchReportsAndSavedViews = async (): Promise<void> => {
    const { getReports, entityId, getSavedViews, entity, cloneEmbedById, includeMultiDatasetReports } = this.props;
    //Fetch Non-Dimensional + MDS + Non-MDS Account Summary Reports
    getReports({
      entityId,
      includeAccountSummaryReports: true,
      includeMultiDatasetReports
    }).then(async (reportsResult) => {
      const reports = reportsResult as ICurrentOrLegacyReport[];
      const { embedId } = this.state;
      if (embedId) {
        let savedViewParams:
          | {
            reportId?: string;
            legacyReportId?: string;
            reportVersion: string;
            entity?: IEntity;
          }
          | Record<string, never> = {};

        cloneEmbedById({ id: embedId })
          .then((embed: IEmbed) => {
            const { reportId, savedViewId, description, type, title } = embed.data;
            const selectedReport = reports.find((report) => report.id === reportId);
            if (!!selectedReport) {
              this.setState({ customTableTile: title });
              if (embed.data.report_version === 'v1') {
                if (selectedReport.legacyReportType !== 'grid') {
                  this.setState({ isAccountsCodeSwitchVisible: true });
                }
                savedViewParams = {
                  legacyReportId: selectedReport.legacyReportId,
                  reportVersion: embed.data.report_version,
                  entity
                };
              } else {
                savedViewParams = { reportId: embed.data.reportId, reportVersion: embed.data.report_version, entity };
                if (type === EMBED_REQUEST_TYPE.DetailTable) {
                  this.setState({ isNonDimensionalReportSelected: true });
                }
              }
              getSavedViews(savedViewParams).then((data) => {
                const selectedSavedView = data.find((savedView) => savedView.id === savedViewId);
                if (!selectedSavedView) {
                  this.showErrorMessage(SAVED_VIEW_FETCH_ERROR);
                  this.setState({ isLoadingValuesFromEmbed: false });
                }
                if (selectedSavedView) {
                  void this.setSelectedReportAndSavedView(
                    selectedSavedView,
                    selectedReport,
                    description,
                    type as EMBED_REQUEST_TYPE
                  );
                }
              });
            } else {
              this.showErrorMessage(REPORT_FETCH_ERROR);
              this.setState({ isLoadingValuesFromEmbed: false });
            }
          })
          .catch(() => {
            this.setState({
              report: reportResetValue,
              savedView: savedViewResetValue,
              hasFailedReportEmbed: true,
              isUpdating: true,
              isLoadingValuesFromEmbed: false,
              disableSaveButton: true,
              isDeleteEmbedInProgress: false
            });
          });
      }
    });
  };

  componentDidMount() {
    if (this.props.isReportEmbedListSelected) {
      const embed = this.props.selectedEmbed;
      void this.intializeStoryContentIfUpdateFromReportEmbed(embed);
    } else {
      this.intializeStoryContentIfUpdateTile();
    }

    this.notifyFormValidityChange();
    this.notifyFormLoadingChange();
  }

  componentDidUpdate() {
    this.notifyFormValidityChange();
    this.notifyFormLoadingChange();
  }

  notifyFormValidityChange = () => {
    const { isLoadingValuesFromEmbed } = this.state;
    const { onFormDataChange, isCreatingEmbed } = this.props;
    if (onFormDataChange) {
      const isValid = this.isAddOrUpdateEnabled() && !isCreatingEmbed && !isLoadingValuesFromEmbed;
      onFormDataChange(isValid as boolean);
    }
  };

  notifyFormLoadingChange = () => {
    const { isLoadingValuesFromEmbed } = this.state;
    const { isCreatingEmbed, onFormLoadingChange } = this.props;
    if (onFormLoadingChange) {
      const isLoading = isCreatingEmbed || isLoadingValuesFromEmbed;
      onFormLoadingChange(isLoading);
    }
  };

  isEmbedLoaded = (embed: IEmbed) => {
    return !!(embed.id || embed?.data?.id) && this.state.report.id && this.state.savedView.id;
  };

  isAddOrUpdateEnabled = () => {
    const { isCreatingEmbed, embed } = this.props;
    const { isLoadingValuesFromEmbed, tableTypeRequest } = this.state;
    const embedType: string = embed?.type || embed?.data?.type;
    const componentPropsData = embed?.data?.sdk?.componentProps?.data || embed?.data?.componentProps?.data;
    if (isLoadingValuesFromEmbed) {
      return true;
    }
    if (
      embedType === EMBED_REQUEST_TYPE.DetailTable &&
      tableTypeRequest === EMBED_REQUEST_TYPE.DetailTable &&
      componentPropsData?.columns.length > DETAIL_TABLE_MAX_COLUMN_LIMIT
    ) {
      return false;
    }
    return isCreatingEmbed ? true : this.isEmbedLoaded(embed);
  };

  handleModalClose = (): void => {
    const { onClose, resetCreateEmbedRequestStatus } = this.props;
    resetCreateEmbedRequestStatus();
    this.setState({ report: reportResetValue, savedView: savedViewResetValue }, onClose);
  };

  render() {
    const {
      isUpdating,
      isLoadingValuesFromEmbed,
      isAccountsCodeSwitchVisible,
      tableTypeRequest,
      showDeleteModal,
      disableSaveButton,
      isDeleteEmbedInProgress
    } = this.state;
    const {
      messages,
      isCreatingEmbed,
      isDetailsTableEmbedEnabled,
      embed,
      entityId,
      isReportEmbedListSelected,
      shouldOnlyRenderInnerContent
    } = this.props;
    const {
      addTableTile,
      updateTableTile,
      selectToCreateTable,
      cancel,
      add,
      update,
      remove,
      detailTableMaxColumnErrorTitle,
      detailTableMaxColumnErrorBody
    } = messages;

    const content = (
      <div className={cx(shouldOnlyRenderInnerContent ? styles.nonModalContentContainer : styles.container)}>
        <div className={styles.modalSubtitle}>{selectToCreateTable}</div>
        {embed?.type === EMBED_REQUEST_TYPE.DetailTable &&
          tableTypeRequest === EMBED_REQUEST_TYPE.DetailTable &&
          embed?.data?.sdk?.componentProps?.data?.columns.length > DETAIL_TABLE_MAX_COLUMN_LIMIT && (
            <div className={styles.detailTableMaxColumnLimitErrorContainer} data-test="detail-table-max-limit-error">
              <div>
                <Icon icon="status-error" className={styles.errorIcon} />
              </div>
              <Text size="minimum" font="condensed">
                <div className={styles.errorTitle}>{detailTableMaxColumnErrorTitle}</div>
                <div className={styles.detailTableMaxColumnLimitError}>{detailTableMaxColumnErrorBody}</div>
              </Text>
            </div>
          )}
        <Form>
          {this.renderReportDropdown()}
          {this.renderSavedViewSection()}
          {isDetailsTableEmbedEnabled && this.renderTableType()}
          {this.renderTitleInput()}
          {this.renderDataUpdateFrequencyDropdown()}
          <div className={styles.switchOptions}>
            {this.renderReportLinkSection()}
            {isAccountsCodeSwitchVisible && this.renderDisplayAccountCodesSection()}
          </div>
          {!shouldOnlyRenderInnerContent ? (
            <>
              <div className={styles.separator} />
              <div className={styles.actionButtonsContainer}>
                <div className={styles.actionButtons}>
                  <Button onClick={this.handleCancel} data-test="report-table-cancel">
                    {cancel}
                  </Button>
                  <span className={styles.addButton}>
                    <Button
                      variant="primary"
                      onClick={this.handleAdd}
                      disabled={!this.isAddOrUpdateEnabled() || disableSaveButton}
                      loading={isCreatingEmbed || isLoadingValuesFromEmbed}
                      dataTest="submit-button"
                    >
                      {isUpdating ? update : add}
                    </Button>
                  </span>
                </div>
                {isUpdating && (
                  <Button
                    onClick={this.props.isReportEmbedListSelected ? this.handleEmbedRemove : this.handleRemove}
                    variant="destructive"
                    dataTest="report-table-remove"
                    loading={isDeleteEmbedInProgress}
                    disabled={isCreatingEmbed || isLoadingValuesFromEmbed}
                  >
                    {remove}
                  </Button>
                )}
              </div>
            </>
          ) : null}
        </Form>
      </div>
    );
    return (
      <>
        {showDeleteModal &&
          createPortal(
            <RemoveEmbedModal
              entityId={entityId}
              onClose={this.handleDeleteModalClose}
              deleteReportEmbed={this.handleRemove}
              messages={this.props.messages}
              isLoading={this.state.isDeleteEmbedInProgress}
            />,
            document.getElementById('app')
          )}

        {shouldOnlyRenderInnerContent ? (
          content
        ) : (
          <Modal
            title={isUpdating || isReportEmbedListSelected ? updateTableTile : addTableTile}
            onClose={this.handleModalClose}
          >
            {content}
          </Modal>
        )}
      </>
    );
  }
}

export type AddTableTileHandle = {
  handleAdd: () => Promise<void>;
};

export const AddTableTileWithRef = forwardRef<AddTableTileHandle, IAddTableTileProps>((props, ref) => {
  const componentRef = useRef<AddTableTile | null>(null);

  useImperativeHandle(ref, () => ({
    handleAdd: async () => {
      if (componentRef.current) {
        await componentRef.current.handleAdd();
      }
    }
  }));

  return <AddTableTile {...props} ref={(instance) => (componentRef.current = instance)} />;
});

AddTableTileWithRef.displayName = 'AddTableTileWithRef';
